import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useInventoryTransactions } from '../../../hooks/inventory/useInventoryTransactions';
import { useDeleteInventoryTransaction } from '../../../hooks/inventory/useDeleteInventoryTransaction';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getInventoryTransactionById } from '../../../api/inventoryService';
import InventoryTransactionForm from '../forms/InventoryTransactionForm';
import InventoryTransactionView from './InventoryTransactionView';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InventoryTransactions() {
    const { t } = useTranslation('common'); 
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        resetParent();
    };

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");

    const [selectedTransaction, setSelectedTransaction] = React.useState(null);

    const handleListItemClick = (transaction) => {
        setViewOpen(true);
        setSelectedTransaction(transaction);
    };

    const { showSnackbar } = useSnackbar();

    const { transactions, loading, loadMore, searchFunction, resetParent } = useInventoryTransactions(open, 1, 10);

    const { remove } = useDeleteInventoryTransaction(resetParent);

    const handleAdd = () => {
        setSelectedTransaction(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedTransaction(item);
        setFormMode("edit");
        setFormOpen(true);
    };

    const onSaveSuccess = async (savedTransaction) => {
        await resetParent();
        const updatedTransaction = await getInventoryTransactionById(savedTransaction.id);
        setSelectedTransaction(updatedTransaction);
        if (!viewOpen) setViewOpen(true);
    };

    const handleDelete = async (transaction) => {
        try {
            await remove(transaction.id);
            showSnackbar(t('inventory.transactions.messages.delete_success', { name: transaction.reference_code }), "success");
        } catch (err) {
            showSnackbar(t('inventory.transactions.messages.delete_fail', { name: transaction.reference_code }), "error");
        }
    };

    return (
        <React.Fragment>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <CompareArrowsIcon fontSize='large' color='secondary'/>
            </ListItemIcon>
            <ListItemText 
                primary="Transactions"
                secondary="view and manage inventory transaction"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={2}>
                <Typography variant="h6">Inventory Transactions</Typography>
                <SearchBox searchFunction={searchFunction} resetParent={resetParent} />
                <Divider />
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                <List sx={{ minHeight: 500 }}>
                    {transactions?.data?.map((transaction) => (
                    <ListItem key={transaction.id}>
                        <ListItemButton onClick={() => {if (userHasAccess(SUB_MODULES.TRANSACTIONS, 1)) handleListItemClick(transaction)}}>
                            <ListItemIcon>
                                {transaction.flow === 'in' ? 
                                <ArrowRightAltIcon fontSize='large' color='success' />
                                :
                                <ArrowRightAltIcon fontSize='large' color='error' sx={{ transform: 'rotate(180deg)' }}/>
                                }
                            </ListItemIcon>
                            <ListItemText
                            primary={
                                <>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    {transaction.reference_code}
                                </Typography><br />
                                <Typography component="span" variant="body1" color="text.primary">
                                    {transaction.item.name}
                                </Typography>
                                </>
                            }
                            secondary={ 
                            <>
                                <Typography component="span" variant="body2" color="text.primary">
                                {`${transaction.flow === 'in' ? "+" : "-"}${removeZeroDecimals(transaction.quantity)}${transaction.item.uom} ${transaction.transactionType.name}`}
                                </Typography><br />
                                <Typography component="span" variant="body2" color="text.secondary">
                                {transaction.date}
                                </Typography>
                            </>
                             }
                            />
                        </ListItemButton>
                    </ListItem>
                    ))}
                </List>

                {/* Load More only shows if more pages exist */}
                {transactions?.pagination?.page < transactions?.pagination?.totalPages && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <IconButton 
                            onClick={loadMore}
                            sx={{
                                width: '100%',              // fill container width
                                justifyContent: 'center',   // center the icon horizontally
                                color: 'primary.main',      // use theme's primary color
                            }}
                        >
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Box>
                )}
                </>
            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.TRANSACTIONS} minAccessLevel={2}>
                <Button onClick={handleAdd}>Add Transaction</Button>
              </RequirePermission>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <InventoryTransactionView 
            handleEdit={handleEdit}
            transaction={selectedTransaction}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <InventoryTransactionForm
            successCallback={onSaveSuccess}
            transaction={selectedTransaction}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
        />
        </React.Fragment>
    );
}